import { PageHeader } from '../components/PageHeader';
import { BuildingOffice2Icon } from '@heroicons/react/24/outline';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import backend from '../services/backend';
import { SortableTable } from '../components/SortableTable';
import { TableSkeleton } from '../components/Skeleton';
import { Link } from '../components/Link';
import { ID } from '../components/ID';
import { Input } from '../catalyst/input';
import { useDeferredValue, useState } from 'react';
import { formatDateTime } from '../lib/dateFormat';

export default function Organisations() {
  useDocumentTitle('Organisations');
  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);

  return (
    <div>
      <PageHeader
        title="Organisations"
        description="Organisations linked to projects via uploads"
        icon={<BuildingOffice2Icon className="size-6" />}
      >
        <div className="w-full max-w-md">
          <Input
            placeholder="Search by id or name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search organisations"
          />
        </div>
      </PageHeader>

      <div className="grid grid-cols-1 gap-4">
        <OrganisationsTable search={deferredSearch} />
      </div>
    </div>
  );
}

function OrganisationsTable({ search }: { search: string }) {
  const query = useQuery({
    queryKey: ['organisations', search],
    queryFn: () =>
      backend
        .listOrganisations({
          limit: 100,
          search: search.trim() || undefined,
        })
        .then((res) => res.data),
    placeholderData: keepPreviousData,
    refetchInterval: 10_000,
  });

  if (query.isLoading) {
    return <TableSkeleton />;
  }

  if (query.error && !query.data) {
    return (
      <div className="text-red-600 dark:text-red-400 text-center text-sm pt-12">{(query.error as Error).message}</div>
    );
  }

  if (!query.data?.items?.length) {
    return (
      <div className="text-gray-500 dark:text-zinc-400 text-center text-xl pt-12 font-extralight">
        No organisations found.
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg">
      <SortableTable
        storageId="organisations"
        limitHeight={false}
        data={query.data?.items ?? []}
        columns={[
          {
            accessor: 'id',
            label: 'ID',
            render: (row) => (
              <Link to={`/organisations/${encodeURIComponent(row.id)}`} className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
                <ID id={row.id} />
              </Link>
            ),
          },
          {
            accessor: 'name',
            label: 'Name',
            render: (row) => <span className="text-gray-900 dark:text-zinc-100">{row.name}</span>,
          },
          {
            accessor: 'projectCount',
            label: 'Projects',
            render: (row) => row.projectCount,
          },
          {
            accessor: 'updatedAt',
            label: 'Updated',
            render: (row) => formatDateTime(row.updatedAt),
          },
        ]}
        rowKey={(row) => row.id}
      />
    </div>
  );
}
